import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Progress,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiBox,
  FiDownload,
  FiPackage,
  FiRefreshCw,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { WSMedia } from "../../../services/remote/media/WSMedia";
import WSMobileApp from "../../../services/remote/mobileapp/WSMobileApp";
import { MobileApp } from "../../../entity/MobileApp";
import { COLORS } from "../../../utils/Colors";

const MAX_FILE_SIZE_MB = 150;

const compareVersions = (a: string, b: string) => {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pb[i] ?? 0) - (pa[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const MobileAppManager = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const mediaInstance = WSMedia.getInstance();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [versions, setVersions] = useState<MobileApp[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const [generatedUrl, setGeneratedUrl] = useState<string>("");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [versionNumber, setVersionNumber] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [pendingDeleteVersion, setPendingDeleteVersion] = useState<
    string | null
  >(null);
  const [deletingVersion, setDeletingVersion] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const fetchMobileApp = async () => {
    setLoadingList(true);
    try {
      const result = await WSMobileApp.getAllVersions();
      const sorted = [...(result ?? [])].sort((a, b) =>
        compareVersions(a.version, b.version),
      );
      setVersions(sorted);
    } catch (error) {
      console.error("Failed to get mobile app:", error);
      toast({
        title: t("mobileApp.load_failed", "Couldn't load versions"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchMobileApp();
  }, []);

  const resetForm = () => {
    setSelectedFile(null);
    setVersionNumber("");
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".apk")) {
      setErrors((prev) => ({ ...prev, file: "File must be an .apk" }));
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: `File must be under ${MAX_FILE_SIZE_MB} MB`,
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, file: "" }));
    setSelectedFile(file);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!selectedFile) next.file = "Select an APK file";
    if (!versionNumber.trim()) next.version = "Version is required";
    else if (!/^\d+\.\d+\.\d+$/.test(versionNumber.trim())) {
      next.version = "Use semantic format, e.g. 1.2.0";
    } else if (versions.some((v) => v.version === versionNumber.trim())) {
      next.version = "This version already exists";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleUpload = async () => {
    if (!validate() || !selectedFile) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const publicUrl = await WSMedia.getInstance().saveFileWithProgress(
        generatedUrl,
        selectedFile,
        (percent) => setUploadProgress(percent),
      );

      await WSMobileApp.uploadVersion({
        version: versionNumber.trim(),
        url: publicUrl,
      });

      toast({
        title: t("mobileApp.upload_success", "Version uploaded"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      fetchMobileApp();
    } catch (error) {
      console.error("Failed to upload mobile app:", error);
      toast({
        title: t("mobileApp.upload_failed", "Upload failed"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = (version: string) => {
    setPendingDeleteVersion(version);
    onOpen();
  };

  const deleteMobileApp = async (version: string) => {
    setDeletingVersion(version);
    try {
      await WSMobileApp.deleteVersion(version);
      setVersions((prev) => prev.filter((v) => v.version !== version));
      toast({
        title: t("mobileApp.delete_success", "Version deleted"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete mobile app:", error);
      toast({
        title: t("mobileApp.delete_failed", "Couldn't delete version"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingVersion(null);
      setPendingDeleteVersion(null);
      onClose();
    }
  };

  const latestVersion = versions[0]?.version;

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const url = await mediaInstance.getGeneratedURL();
        setGeneratedUrl(url);
      } catch (error) {
        console.error("Failed to get generated URL:", error);
      }
    };

    fetchUrl();
  }, []);

  return (
    <Box minH="100vh" bg="gray.50" p={{ base: 4, md: 6, lg: 8 }}>
      <Box maxW="1100px" mx="auto">
        {/* Page header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
          mb={7}
        >
          <Box>
            <HStack spacing={3} mb={2}>
              <Flex
                w="42px"
                h="42px"
                borderRadius="xl"
                bg="orange.50"
                color={COLORS.Brand.Primary}
                align="center"
                justify="center"
              >
                <FiPackage size={21} />
              </Flex>
              <Heading size="lg" color="gray.800">
                {t("mobileApp.title", "Mobile App Manager")}
              </Heading>
            </HStack>
            <Text color="gray.500" fontSize="sm">
              {t(
                "mobileApp.subtitle",
                "Upload new APK builds and manage released versions.",
              )}
            </Text>
          </Box>

          <Badge
            px={3}
            py={1.5}
            borderRadius="full"
            colorScheme="green"
            fontSize="xs"
            fontWeight="600"
          >
            {versions.length} {versions.length === 1 ? "release" : "releases"}
          </Badge>
        </Flex>

        {/* Overview cards */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            p={4}
            boxShadow="sm"
          >
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
              Latest version
            </Text>
            <Text fontSize="xl" fontWeight="700" color="gray.800">
              {latestVersion || "—"}
            </Text>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            p={4}
            boxShadow="sm"
          >
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
              Published builds
            </Text>
            <Text fontSize="xl" fontWeight="700" color="gray.800">
              {versions.length}
            </Text>
          </Box>

          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            p={4}
            boxShadow="sm"
          >
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
              Maximum APK size
            </Text>
            <Text fontSize="xl" fontWeight="700" color="gray.800">
              {MAX_FILE_SIZE_MB} MB
            </Text>
          </Box>
        </Box>

        {/* Upload section */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="sm"
          mb={6}
        >
          <Box
            px={{ base: 5, md: 6 }}
            py={5}
            borderBottom="1px solid"
            borderColor="gray.100"
            bg="gray.50"
          >
            <HStack spacing={3}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="lg"
                bg="orange.100"
                color={COLORS.Brand.Primary}
                align="center"
                justify="center"
              >
                <FiUploadCloud size={18} />
              </Flex>
              <Box>
                <Heading size="sm" color="gray.800">
                  {t("mobileApp.new_version", "Upload New Version")}
                </Heading>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  Publish a new APK build to your users.
                </Text>
              </Box>
            </HStack>
          </Box>

          <Box p={{ base: 5, md: 6 }}>
            <VStack spacing={5} align="stretch">
              <Box
                display="grid"
                gridTemplateColumns={{ base: "1fr", md: "220px 1fr" }}
                gap={5}
                alignItems="start"
              >
                <FormControl isInvalid={!!errors.version}>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="600"
                    color="gray.700"
                    mb={2}
                  >
                    {t("mobileApp.version_label", "Version number")}
                  </FormLabel>
                  <Input
                    value={versionNumber}
                    onChange={(e) => {
                      setVersionNumber(e.target.value);
                      setErrors((prev) => ({ ...prev, version: "" }));
                    }}
                    placeholder="1.4.0"
                    isDisabled={uploading}
                    size="md"
                    borderRadius="lg"
                    focusBorderColor={COLORS.Brand.Primary}
                  />
                  {errors.version && (
                    <Text fontSize="xs" color="red.500" mt={1.5}>
                      {errors.version}
                    </Text>
                  )}
                  {!errors.version && (
                    <Text fontSize="xs" color="gray.400" mt={1.5}>
                      Semantic version: major.minor.patch
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.file}>
                  <FormLabel
                    fontSize="sm"
                    fontWeight="600"
                    color="gray.700"
                    mb={2}
                  >
                    {t("mobileApp.file_label", "APK file")}
                  </FormLabel>

                  {!selectedFile ? (
                    <Box
                      border="1.5px dashed"
                      borderColor="gray.300"
                      borderRadius="xl"
                      bg="gray.50"
                      px={5}
                      py={7}
                      textAlign="center"
                      cursor={uploading ? "not-allowed" : "pointer"}
                      opacity={uploading ? 0.6 : 1}
                      onClick={() =>
                        !uploading && fileInputRef.current?.click()
                      }
                      _hover={
                        uploading
                          ? {}
                          : {
                              borderColor: COLORS.Brand.Primary,
                              bg: "orange.50",
                            }
                      }
                      transition="all 0.2s"
                    >
                      <Flex
                        mx="auto"
                        w="44px"
                        h="44px"
                        borderRadius="xl"
                        bg="white"
                        color={COLORS.Brand.Primary}
                        align="center"
                        justify="center"
                        boxShadow="sm"
                      >
                        <FiUploadCloud size={22} />
                      </Flex>
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        color="gray.700"
                        mt={3}
                      >
                        Choose an APK file
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {t(
                          "mobileApp.drop_hint",
                          "Click to select an .apk file (max {{size}} MB)",
                          { size: MAX_FILE_SIZE_MB },
                        )}
                      </Text>
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept=".apk"
                        display="none"
                        onChange={handleFileChange}
                      />
                    </Box>
                  ) : (
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={3}
                      border="1px solid"
                      borderColor="orange.100"
                      bg="orange.50"
                      borderRadius="xl"
                      p={4}
                    >
                      <HStack spacing={3} minW={0}>
                        <Flex
                          flexShrink={0}
                          w="40px"
                          h="40px"
                          borderRadius="lg"
                          bg="white"
                          color={COLORS.Brand.Primary}
                          align="center"
                          justify="center"
                        >
                          <FiPackage size={19} />
                        </Flex>
                        <Box minW={0}>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.800"
                            noOfLines={1}
                          >
                            {selectedFile.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500" mt={0.5}>
                            {formatBytes(selectedFile.size)} • APK
                          </Text>
                        </Box>
                      </HStack>

                      {!uploading && (
                        <IconButton
                          aria-label="Remove file"
                          icon={<FiX />}
                          size="sm"
                          variant="ghost"
                          borderRadius="lg"
                          onClick={resetForm}
                        />
                      )}
                    </Flex>
                  )}

                  {errors.file && (
                    <Text fontSize="xs" color="red.500" mt={1.5}>
                      {errors.file}
                    </Text>
                  )}
                </FormControl>
              </Box>

              {uploading && (
                <Box
                  bg="gray.50"
                  borderRadius="xl"
                  p={4}
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <Flex justify="space-between" mb={2}>
                    <HStack spacing={2}>
                      <Spinner size="xs" color={COLORS.Brand.Primary} />
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        {t("mobileApp.uploading", "Uploading...")}
                      </Text>
                    </HStack>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={COLORS.Brand.Primary}
                    >
                      {uploadProgress}%
                    </Text>
                  </Flex>
                  <Progress
                    value={uploadProgress}
                    size="sm"
                    borderRadius="full"
                    bg="gray.200"
                    sx={{
                      "& > div": { backgroundColor: COLORS.Brand.Primary },
                    }}
                  />
                </Box>
              )}

              <Flex
                justify="flex-end"
                pt={1}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <Button
                  mt={4}
                  leftIcon={<FiUploadCloud />}
                  bg={COLORS.Brand.Primary}
                  color="white"
                  borderRadius="lg"
                  px={6}
                  _hover={{
                    bg: COLORS.Brand.Primary,
                    transform: "translateY(-1px)",
                  }}
                  _active={{
                    bg: COLORS.Brand.Primary,
                    transform: "translateY(0)",
                  }}
                  transition="all 0.15s"
                  onClick={handleUpload}
                  isLoading={uploading}
                  loadingText={t("mobileApp.uploading", "Uploading...")}
                  isDisabled={!selectedFile || !versionNumber.trim()}
                >
                  {t("mobileApp.upload_button", "Upload Version")}
                </Button>
              </Flex>
            </VStack>
          </Box>
        </Box>

        {/* Versions list */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="sm"
        >
          <Flex
            align={{ base: "flex-start", sm: "center" }}
            justify="space-between"
            direction={{ base: "column", sm: "row" }}
            gap={3}
            px={{ base: 5, md: 6 }}
            py={5}
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            <Box>
              <HStack spacing={2}>
                <Heading size="sm" color="gray.800">
                  {t("mobileApp.released_versions", "Released Versions")}
                </Heading>
                {versions.length > 0 && (
                  <Badge colorScheme="gray" borderRadius="full">
                    {versions.length}
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Manage and download previously published builds.
              </Text>
            </Box>

            <IconButton
              aria-label="Refresh"
              icon={<FiRefreshCw />}
              size="sm"
              variant="outline"
              borderRadius="lg"
              onClick={fetchMobileApp}
              isLoading={loadingList}
            />
          </Flex>

          {loadingList ? (
            <Flex justify="center" align="center" direction="column" py={20}>
              <Spinner size="md" color={COLORS.Brand.Primary} />
              <Text fontSize="sm" color="gray.500" mt={3}>
                Loading releases...
              </Text>
            </Flex>
          ) : versions.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={20}
              px={6}
              color="gray.400"
            >
              <Flex
                w="52px"
                h="52px"
                borderRadius="xl"
                bg="gray.100"
                align="center"
                justify="center"
              >
                <FiBox size={24} />
              </Flex>
              <Text mt={4} fontSize="sm" fontWeight="600" color="gray.600">
                {t("mobileApp.empty", "No versions uploaded yet")}
              </Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                Upload your first APK to create a release.
              </Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr bg="gray.50">
                    <Th
                      py={3}
                      textTransform="none"
                      fontSize="xs"
                      color="gray.500"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      {t("mobileApp.col_version", "Version")}
                    </Th>
                    <Th
                      py={3}
                      textTransform="none"
                      fontSize="xs"
                      color="gray.500"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      {t("mobileApp.col_uploaded", "Uploaded")}
                    </Th>
                    <Th
                      py={3}
                      textTransform="none"
                      fontSize="xs"
                      color="gray.500"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      {t("mobileApp.col_file", "File")}
                    </Th>
                    <Th
                      py={3}
                      textTransform="none"
                      fontSize="xs"
                      color="gray.500"
                      textAlign="right"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      {t("mobileApp.col_actions", "Actions")}
                    </Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {versions.map((v) => (
                    <Tr
                      key={v.id}
                      _hover={{ bg: "gray.50" }}
                      transition="background 0.15s"
                    >
                      <Td py={4}>
                        <HStack spacing={2}>
                          <Flex
                            w="34px"
                            h="34px"
                            borderRadius="lg"
                            bg={
                              v.version === latestVersion
                                ? "green.50"
                                : "gray.100"
                            }
                            color={
                              v.version === latestVersion
                                ? "green.600"
                                : "gray.500"
                            }
                            align="center"
                            justify="center"
                          >
                            <FiPackage size={16} />
                          </Flex>
                          <Box>
                            <Text
                              fontWeight="700"
                              color="gray.800"
                              fontSize="sm"
                            >
                              {v.version}
                            </Text>
                            {v.version === latestVersion && (
                              <Badge
                                mt={1}
                                colorScheme="green"
                                borderRadius="full"
                                fontSize="9px"
                                px={2}
                              >
                                <HStack spacing={1}>
                                  <FiCheckCircle size={10} />
                                  <Text>{t("mobileApp.latest", "Latest")}</Text>
                                </HStack>
                              </Badge>
                            )}
                          </Box>
                        </HStack>
                      </Td>

                      <Td color="gray.600" fontSize="sm">
                        {new Date(v.createdAt!).toLocaleDateString(undefined, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Td>

                      <Td>
                        <Button
                          as="a"
                          href={v.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          variant="ghost"
                          color="blue.500"
                          leftIcon={<FiDownload size={14} />}
                          borderRadius="lg"
                          _hover={{ bg: "blue.50" }}
                        >
                          {t("mobileApp.download_link", "Download")}
                        </Button>
                      </Td>

                      <Td textAlign="right">
                        <IconButton
                          aria-label="Delete version"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          borderRadius="lg"
                          isLoading={deletingVersion === v.version}
                          onClick={() => confirmDelete(v.version)}
                          _hover={{ bg: "red.50" }}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("mobileApp.delete_title", "Delete Version")}
            </AlertDialogHeader>
            <AlertDialogBody color="gray.600">
              {t(
                "mobileApp.delete_confirm",
                "Are you sure you want to delete version {{version}}? This can't be undone.",
                { version: pendingDeleteVersion },
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} borderRadius="lg">
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                borderRadius="lg"
                isLoading={!!deletingVersion}
                onClick={() =>
                  pendingDeleteVersion && deleteMobileApp(pendingDeleteVersion)
                }
              >
                {t("common.delete", "Delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
